// Importing dayjs is generally restricted in favor of using the `getDayjs` utility function in here.
/* eslint-disable no-restricted-imports */
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import duration from 'dayjs/plugin/duration';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localeData from 'dayjs/plugin/localeData';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import 'dayjs/locale/et';
import { PeriodArray } from '../types';
import CustomParseFormat from 'dayjs/plugin/customParseFormat';
import isBetweenPlugin from 'dayjs/plugin/isBetween';

dayjs.extend(isBetweenPlugin);
dayjs.extend(CustomParseFormat);
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(quarterOfYear);
dayjs.extend(isoWeek);
dayjs.extend(isSameOrBefore);
dayjs.extend(duration);
dayjs.extend(localeData);
dayjs.locale('et');

export const setDefaultTimezone = (timezone: string) =>
  dayjs.tz.setDefault(timezone);
export const getDuration = (milliseconds: number) =>
  dayjs.duration(milliseconds);
export const getUtcDayjs = (date: dayjs.ConfigType) => dayjs.utc(date);
export const getDayjsMonths = () => dayjs.months();
export const getDayjsWeekdays = () => dayjs.weekdays(true);
export const getDayjsWeekdaysShort = () => dayjs.weekdaysShort(true);

export type FormattedTime =
  `${number}${number}:${number}${number}:${number}${number}`;
export type FormattedTimeShort = `${number}${number}:${number}${number}`;
export type FormattedDate =
  `${number}${number}.${number}${number}.${number}${number}${number}${number}`;
export type FormattedDateTime = `${FormattedDate} ${FormattedTime}`;
export type ISODate =
  `${number}${number}${number}${number}-${number}${number}-${number}${number}`;
export type ISODateTime =
  `${ISODate}T${number}${number}:${number}${number}:${number}${number}.${number}${number}${number}Z`;

export const DATE_FORMAT = 'DD.MM.YYYY';
export const TIME_FORMAT = 'HH:mm:ss';
export const DATE_TIME_FORMAT = `${DATE_FORMAT} ${TIME_FORMAT}`;
export const DATE_TIME_FORMAT_HM = `${DATE_FORMAT} HH:mm`;
export const DATE_TIME_FORMAT_EXTRA_SPACE = `${DATE_FORMAT}  ${TIME_FORMAT}`;
export const ISO_DATE_FORMAT = 'YYYY-MM-DD';
export const ISO_DATE_TIME_FORMAT = `${ISO_DATE_FORMAT}THH:mm:ss.sssZ`;
export const ISO_DATE_TIME_WITHOUT_MILLISECONDS_FORMAT = `${ISO_DATE_FORMAT}THH:mm:ssZ`;

const FORMATTED_DATE_REGEX = /[0-3]\d\.[01]\d\.\d{4}/;
const FORMATTED_TIME_REGEX = /[0-2]\d:[0-5]\d:[0-5]\d/;
const FORMATTED_TIME_SHORT_REGEX = /[0-2]\d:[0-5]\d/;

// https://262.ecma-international.org/5.1/#sec-15.9.1.1
export const MAX_TIMESTAMP = 864e13;
export const MIN_TIMESTAMP = -MAX_TIMESTAMP;

export const getDayjsNow = () => getDayjs(new Date())!;

export const getDayjsFromAny = (
  date?: Date | dayjs.Dayjs | string | number | null,
): Dayjs | null => {
  if (isFormattedDateTime(date)) {
    return getDayjs(date, DATE_TIME_FORMAT);
  } else if (isFormattedDateTimeShort(date)) {
    return getDayjs(date, DATE_TIME_FORMAT_HM);
  } else if (isFormattedDate(date)) {
    return getDayjs(date, DATE_FORMAT);
  } else {
    return getDayjs(date);
  }
};

export const getDayjs = (
  date?: Date | dayjs.Dayjs | string | number | null,
  parseFormat?: string,
  strict?: boolean,
): Dayjs | null => {
  if (date === null || dayjs.isDayjs(date)) {
    return date;
  }

  if (typeof date === 'undefined') {
    return null;
  }

  if (date instanceof Date || (!strict && typeof date === 'number')) {
    return dayjs(date);
  }

  date = date.toString().trim();

  if (
    !parseFormat ||
    (parseFormat?.startsWith(ISO_DATE_FORMAT) && hasIsoTimezone(date))
  ) {
    // Try parsing date string as ISO date without format even if format is provided.
    // In case ISO date format (YYYY-MM-DD) is provided we want to parse it as full ISO datetime string to take timezone into account.
    const isoDayjs = dayjs(date).tz(getAppTimezone(), !hasIsoTimezone(date));
    if (isoDayjs.isValid()) {
      return isoDayjs;
    }
  }

  // If parsing as ISO date fails, try parsing with format if provided
  if (parseFormat) {
    // Because dayjs parsing with format is slow, we want to avoid it if possible. (Except when strict mode is on)
    if (!strict && parseFormat === DATE_FORMAT) {
      date = date.split('.').reverse().join('-');
      parseFormat = undefined;
    }

    // Constructing dayjs via regular constructor (with browser timezone) and then shifting it to app timezone without adjusting hours
    // because dayjs.tz() constructor does not support `strict` flag
    const formatDayjs = dayjs(date, parseFormat, strict).tz(
      getAppTimezone(),
      true,
    );
    if (formatDayjs.isValid()) {
      return formatDayjs;
    }
  }

  return null;
};

// Configured timezone is set in useConfigurationQuery.onSuccess
const hasIsoTimezone = (date: string) =>
  /([+-]\d{2}:\d{2}|Z|UTC|GMT)/.test(date);

// Configured timezone is set in useConfigurationQuery.onSuccess
const getAppTimezone = () => (dayjs().tz() as any).$x.$timezone;

export const getIsoDate = <T extends Date | FormattedDate | null>(
  date: T,
): T extends null ? null : ISODate =>
  (date === null
    ? null
    : getDayjs(date, isFormattedDate(date) ? DATE_FORMAT : undefined)?.format(
        ISO_DATE_FORMAT,
      ) || null) as any;

export const getIsoDateFromFormattedDate = <T extends FormattedDate | null>(
  date: T,
): T extends null ? null : ISODate =>
  getDayjs(date, DATE_FORMAT)?.format(ISO_DATE_FORMAT) || (null as any);

export const getIsoDateFromFormattedDateTime = <
  T extends FormattedDateTime | null,
>(
  dateTime: T,
): T extends null ? null : ISODateTime => {
  if (!dateTime) {
    return null as any;
  }

  const [date, time] = dateTime.split('  ', 2) ?? [];
  const [day, month, year] = date.split('.').map((part) => Number(part));
  const [hours, minutes, seconds] = time
    ?.split(':')
    .map((part) => Number(part)) ?? [0, 0, 0];

  return new Date(
    year,
    month - 1,
    day,
    hours,
    minutes,
    seconds,
    0,
  ).toISOString() as any;
};

export const getIsoDateToday = () =>
  getDayjsNow().format(ISO_DATE_FORMAT) as ISODate;

export const formatIsoDate = (dateString?: string | null) =>
  getDayjs(dateString)?.format(ISO_DATE_FORMAT) || null;

export const getIsoDateString = <T extends Date | null>(
  date: T,
): T extends null ? null : ISODate =>
  (getDayjs(date)?.format(ISO_DATE_FORMAT) || null) as any;

export const formatDate = (dateString?: string | null, format = DATE_FORMAT) =>
  getDayjs(dateString)?.format(format) || null;

export const formatTime = (dateString?: string | null, format = TIME_FORMAT) =>
  getDayjs(dateString)?.format(format) || null;

export const formatDateTime = (
  dateTimeString?: string | null,
  format = DATE_TIME_FORMAT,
) => getDayjs(dateTimeString)?.format(format) || null;

export const formatDateTimeShort = (dateTimeString?: string | null) =>
  formatDate(dateTimeString, DATE_TIME_FORMAT_HM);

export const getValidPeriodString = (
  validPeriod: [string | null, string | null] | undefined,
  format: string = DATE_FORMAT,
) => {
  if (!validPeriod) return '';

  return getDatePeriodString(validPeriod[0], validPeriod[1], format);
};

export const getValidPeriodArray = (
  validPeriod:
    [dayjs.Dayjs | string | null, dayjs.Dayjs | string | null] | undefined,
  format: string = DATE_FORMAT,
) => {
  if (!validPeriod) return [null, null] as PeriodArray;

  return getDatePeriodArray(validPeriod[0], validPeriod[1], format);
};

export const getDatePeriodString = (
  from?: dayjs.Dayjs | string | null,
  to?: dayjs.Dayjs | string | null,
  format: string = DATE_FORMAT,
) => {
  from = getDayjs(from);
  to = getDayjs(to);

  if (!from && !to) return '';

  return `${from?.format(format) || '...'} - ${to?.format(format) || '...'}`;
};

export const getDatePeriodArray = (
  from: dayjs.Dayjs | string | null,
  to: dayjs.Dayjs | string | null,
  format: string = ISO_DATE_FORMAT,
) => {
  from = getDayjs(from, format);
  to = getDayjs(to, format);

  return [from, to] as PeriodArray;
};

export const getDatePeriodFromString = (
  value: string | null,
  format: string = DATE_FORMAT,
) => {
  const [from, to] = value?.split('-') || [null, null];
  return getDatePeriodArray(from, to, format);
};

export const isInDateRange = (
  outerRange: [dayjs.Dayjs | null, dayjs.Dayjs | null],
  innerRange: [dayjs.Dayjs | null, dayjs.Dayjs | null],
) =>
  (outerRange[0] === null ||
    (innerRange[0] !== null && !outerRange[0].isAfter(innerRange[0]))) &&
  (outerRange[1] === null ||
    (innerRange[1] !== null && !outerRange[1].isBefore(innerRange[1])));

export const isValidToday = (
  validPeriod: [ISODate | null, ISODate | null],
): boolean => {
  const today = getIsoDateToday();
  const [from, to] = validPeriod;
  return (!from || from <= today) && (!to || to >= today);
};

export const isIsoDate = (value: any): value is ISODate =>
  typeof value === 'string' && dayjs(value, ISO_DATE_FORMAT, true).isValid();

export const isIsoDateTime = (value: any): value is ISODateTime =>
  typeof value === 'string' &&
  (dayjs(value, ISO_DATE_TIME_FORMAT).isValid() ||
    dayjs(value, ISO_DATE_TIME_WITHOUT_MILLISECONDS_FORMAT).isValid());

export const isFormattedDate = (value: any): value is FormattedDate =>
  typeof value === 'string' && FORMATTED_DATE_REGEX.test(value);

export const isFormattedDateTime = (value: any): value is FormattedDateTime =>
  typeof value === 'string' &&
  value.split(' ').length === 2 &&
  FORMATTED_DATE_REGEX.test(value.split(' ')[0]) &&
  FORMATTED_TIME_REGEX.test(value.split(' ')[1]);

export const isFormattedDateTimeShort = (
  value: any,
): value is FormattedDateTime =>
  typeof value === 'string' &&
  value.split(' ').length === 2 &&
  FORMATTED_DATE_REGEX.test(value.split(' ')[0]) &&
  FORMATTED_TIME_SHORT_REGEX.test(value.split(' ')[1]);
